import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import CustomerHeader from '../components/CustomerHeader';
import CustomAlert from '../components/CustomAlert';
import LoadingSpinner from '../components/LoadingSpinner';
import { FaBox, FaPlay, FaImage, FaBook, FaDownload, FaSearch, FaFilter, FaTimes } from 'react-icons/fa';
import productCatalogService, { Product, Category } from '../services/productCatalogService';
import config from '../config/config';

const PageContainer = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.background};
`;

const ContentContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 1rem;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.primary};
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.5rem 0;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;

  &:hover {
    color: ${props => props.theme.primaryDark || '#c00000'};
  }

  &:active {
    transform: translateX(-2px);
  }
`;

const Title = styled.h2`
  color: ${props => props.theme.primary};
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const SearchSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const SearchContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  font-size: 0.9rem;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }
`;

const SearchButton = styled.button`
  padding: 0.75rem 1rem;
  background: ${props => props.theme.primary};
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  transition: all 0.3s;

  &:hover {
    background: ${props => props.theme.primaryDark};
  }
`;

const FilterTabs = styled.div`
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
  padding-bottom: 0.5rem;
`;

const FilterTab = styled.button<{ active?: boolean }>`
  padding: 0.5rem 1rem;
  border-radius: 20px;
  border: none;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.3s;
  
  ${props => props.active ? `
    background: ${props.theme.primary};
    color: white;
  ` : `
    background: ${props.theme.surface};
    color: ${props.theme.textSecondary};
    
    &:hover {
      background: ${props.theme.border};
    }
  `}
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`;

const ProductCard = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const ProductImage = styled.div`
  height: 120px;
  background: linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  color: ${props => props.theme.primary};
  position: relative;
  overflow: hidden;
`;

const ProductBadge = styled.span`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: ${props => props.theme.primary};
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
`;

const ProductInfo = styled.div`
  padding: 0.75rem;
`;

const ProductName = styled.h4`
  color: ${props => props.theme.textPrimary};
  margin: 0 0 0.25rem;
  font-size: 0.9rem;
`;

const ProductModel = styled.p`
  color: ${props => props.theme.textSecondary};
  font-size: 0.8rem;
  margin: 0 0 0.5rem;
`;

const ProductActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button`
  flex: 1;
  padding: 0.5rem;
  border: 1px solid ${props => props.theme.border};
  background: white;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  transition: all 0.3s;
  color: ${props => props.theme.textPrimary};

  &:hover {
    background: ${props => props.theme.primary};
    color: white;
    border-color: ${props => props.theme.primary};
  }
`;

const CategorySection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const CategoryTitle = styled.h3`
  color: ${props => props.theme.textPrimary};
  font-size: 1.1rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const MediaGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
`;

const MediaCard = styled.div`
  background: ${props => props.theme.surface};
  border-radius: 8px;
  padding: 1rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  border: 2px solid transparent;

  &:hover {
    border-color: ${props => props.theme.primary};
    transform: translateY(-2px);
  }
`;

const MediaIcon = styled.div`
  font-size: 2rem;
  color: ${props => props.theme.primary};
  margin-bottom: 0.5rem;
`;

const MediaTitle = styled.h4`
  color: ${props => props.theme.textPrimary};
  font-size: 0.85rem;
  margin: 0 0 0.25rem;
`;

const MediaDescription = styled.p`
  color: ${props => props.theme.textSecondary};
  font-size: 0.75rem;
  margin: 0;
`;

const ProductModal = styled.div<{ isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: ${props => props.isVisible ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ProductModalContent = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ProductModalHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 2px solid ${props => props.theme.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ProductModalTitle = styled.h2`
  margin: 0;
  font-size: 1.3rem;
  font-weight: 600;
  color: ${props => props.theme.primary};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: ${props => props.theme.textSecondary};
  padding: 0.25rem;
  display: flex;
  align-items: center;
  transition: all 0.2s;

  &:hover {
    color: ${props => props.theme.primary};
  }
`;

const ProductModalBody = styled.div`
  padding: 1.5rem;
`;

const ProductModalImage = styled.img`
  width: 100%;
  height: 250px;
  object-fit: cover;
  border-radius: 12px;
  margin-bottom: 1.5rem;
`;

const ProductDetailRow = styled.div`
  margin-bottom: 1rem;
`;

const ProductDetailLabel = styled.div`
  font-weight: 600;
  color: ${props => props.theme.textSecondary};
  font-size: 0.85rem;
  margin-bottom: 0.25rem;
`;

const ProductDetailValue = styled.div`
  color: ${props => props.theme.textPrimary};
  font-size: 0.95rem;
  line-height: 1.5;
`;

const ProductBadgeGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
`;

const ProductTag = styled.span`
  background: ${props => props.theme.primary};
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-size: 0.75rem;
  font-weight: 600;
`;

const ViewProductInformation: React.FC = () => {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [alert, setAlert] = useState<{
    isVisible: boolean;
    type?: 'success' | 'error' | 'warning' | 'info' | 'confirm';
    title?: string;
    message: string;
    onConfirm?: () => void;
  }>({ isVisible: false, message: '' });

  // Load products and categories on mount
  useEffect(() => {
    loadProductsAndCategories();
  }, []);

  const loadProductsAndCategories = async () => {
    setIsLoading(true);
    try {
      console.log('🔍 Loading products from API...');
      console.log('🔍 API Base URL:', config.API_BASE_URL);

      // Load products and categories in parallel
      const [productsResponse, categoriesResponse] = await Promise.all([
        productCatalogService.getAllProducts(false), // Show all products including unpublished
        productCatalogService.getCategories()
      ]);

      console.log('📦 Products Response:', productsResponse);
      console.log('📂 Categories Response:', categoriesResponse);

      if (productsResponse.success) {
        console.log('✅ Products loaded:', productsResponse.data.length);
        setProducts(productsResponse.data);
      } else {
        console.error('❌ Failed to load products:', productsResponse.message);
        setAlert({
          isVisible: true,
          type: 'error',
          title: 'Error',
          message: productsResponse.message || 'Failed to load products'
        });
      }

      if (categoriesResponse.success) {
        console.log('✅ Categories loaded:', categoriesResponse.data);
        setCategories(categoriesResponse.data);
      }
    } catch (error: any) {
      console.error('❌ Error loading product information:', error);
      setAlert({
        isVisible: true,
        type: 'error',
        title: 'Error',
        message: error.message || 'Failed to load product information'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const mediaResources = [
    {
      id: 'MED-001',
      title: 'Product Catalog',
      description: '2024 Edition',
      type: 'catalog',
      icon: FaBook
    },
    {
      id: 'MED-002',
      title: 'Training Videos',
      description: '15 Videos',
      type: 'video',
      icon: FaPlay
    },
    {
      id: 'MED-003',
      title: 'Product Images',
      description: '200+ Images',
      type: 'image',
      icon: FaImage
    },
    {
      id: 'MED-004',
      title: 'User Manuals',
      description: 'PDF Downloads',
      type: 'manual',
      icon: FaDownload
    }
  ];

  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showProductModal, setShowProductModal] = useState(false);

  const handleProductClick = (productId: string) => {
    const product = products.find(p => p.uid === productId);
    if (product) {
      setSelectedProduct(product);
      setShowProductModal(true);
    }
  };

  const handleMediaClick = (mediaType: string) => {
    setAlert({
      isVisible: true,
      type: 'info',
      title: mediaType,
      message: `Opening ${mediaType} resources...`
    });
  };

  const handleDownload = (productId: string) => {
    const product = products.find(p => p.uid === productId);
    if (product && product.pdfPath) {
      // Construct full PDF URL
      const pdfUrl = `${config.API_BASE_URL}/${product.pdfPath}`.replace(/([^:]\/)\/+/g, "$1");

      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = product.pdfFileName || `${product.title}_specification.pdf`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setAlert({
        isVisible: true,
        type: 'success',
        title: 'Download Started',
        message: `Downloading ${product.pdfFileName || 'specification'}...`
      });
    } else {
      setAlert({
        isVisible: true,
        type: 'warning',
        title: 'No Specification',
        message: 'No PDF specification available for this product.'
      });
    }
  };

  // Helper function to get product icon based on category
  const getProductIcon = (category?: string) => {
    if (!category) return '📦';
    const cat = category.toLowerCase();
    if (cat.includes('cooler')) return '❄️';
    if (cat.includes('fountain') || cat.includes('dispenser')) return '🚰';
    if (cat.includes('vending')) return '🎰';
    if (cat.includes('refrigerator') || cat.includes('fridge')) return '🥤';
    return '📦';
  };

  // Helper function to get badge text
  const getProductBadge = (product: Product) => {
    if (product.isPopular) return 'Popular';
    if (product.isPremium) return 'Premium';
    if (product.isEnergyStar) return 'Energy Star';
    if (product.isPublished) return 'Published';
    return 'New';
  };

  // Filter products based on active filter and search query
  const filteredProducts = products.filter(product => {
    // Filter by category
    const matchesFilter = activeFilter === 'all' ||
      product.category?.toLowerCase() === activeFilter.toLowerCase();

    // Filter by search query
    const matchesSearch = !searchQuery ||
      product.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.modelNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.manufacturer?.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesFilter && matchesSearch;
  });

  return (
    <PageContainer>
      <CustomerHeader skipAuthCheck={true} />
      {isLoading && <LoadingSpinner isVisible={isLoading} />}
      <ContentContainer>
        <BackButton onClick={() => navigate('/dashboard')}>
          ← Back
        </BackButton>
        <Title>Product And Promotion</Title>

        <SearchSection>
          <SearchContainer>
            <SearchInput
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <SearchButton onClick={loadProductsAndCategories}>
              {React.createElement(FaSearch as any, {})} Search
            </SearchButton>
          </SearchContainer>

          <FilterTabs>
            <FilterTab
              active={activeFilter === 'all'}
              onClick={() => setActiveFilter('all')}
            >
              All Products
            </FilterTab>
            {categories.map(category => (
              <FilterTab
                key={category.category}
                active={activeFilter === category.category.toLowerCase()}
                onClick={() => setActiveFilter(category.category.toLowerCase())}
              >
                {category.category} ({category.count})
              </FilterTab>
            ))}
          </FilterTabs>
        </SearchSection>

        <CategorySection>
          <CategoryTitle>
            {React.createElement(FaBox as any, {})}
            Equipment Catalog ({filteredProducts.length})
          </CategoryTitle>
          {filteredProducts.length > 0 ? (
            <ProductGrid>
              {filteredProducts.map(product => {
                // Construct full image URL from API_BASE_URL and imagePath
                const imageUrl = product.imagePath
                  ? `${config.API_BASE_URL}/${product.imagePath}`.replace(/([^:]\/)\/+/g, "$1")
                  : null;

                return (
                  <ProductCard key={product.uid} onClick={() => handleProductClick(product.uid)}>
                    <ProductImage>
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={product.title}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            e.currentTarget.parentElement!.innerHTML = `<span style="font-size: 3rem">${getProductIcon(product.category)}</span>`;
                          }}
                        />
                      ) : (
                        <span style={{ fontSize: '3rem' }}>{getProductIcon(product.category)}</span>
                      )}
                      <ProductBadge>{getProductBadge(product)}</ProductBadge>
                    </ProductImage>
                    <ProductInfo>
                      <ProductName>{product.title}</ProductName>
                      <ProductModel>
                        {product.modelNumber && `${product.modelNumber}`}
                        {product.modelNumber && product.manufacturer && ' • '}
                        {product.manufacturer}
                      </ProductModel>
                      <ProductActions>
                        <ActionButton onClick={(e) => {
                          e.stopPropagation();
                          handleProductClick(product.uid);
                        }}>
                          {React.createElement(FaSearch as any, {})} View
                        </ActionButton>
                        <ActionButton onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(product.uid);
                        }}>
                          {React.createElement(FaDownload as any, {})} Spec
                        </ActionButton>
                      </ProductActions>
                    </ProductInfo>
                  </ProductCard>
                );
              })}
            </ProductGrid>
          ) : (
            <p style={{ textAlign: 'center', color: '#999', padding: '2rem' }}>
              {isLoading ? 'Loading products...' : 'No products found'}
            </p>
          )}
        </CategorySection>
      </ContentContainer>

      {/* Product Details Modal */}
      <ProductModal
        isVisible={showProductModal}
        onClick={() => setShowProductModal(false)}
      >
        <ProductModalContent onClick={(e) => e.stopPropagation()}>
          <ProductModalHeader>
            <ProductModalTitle>{selectedProduct?.title}</ProductModalTitle>
            <CloseButton onClick={() => setShowProductModal(false)}>
              {React.createElement(FaTimes as any, {})}
            </CloseButton>
          </ProductModalHeader>
          <ProductModalBody>
            {selectedProduct && (
              <>
                {selectedProduct.imagePath && (
                  <ProductModalImage
                    src={`${config.API_BASE_URL}/${selectedProduct.imagePath}`.replace(/([^:]\/)\/+/g, "$1")}
                    alt={selectedProduct.title}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                )}

                <ProductBadgeGroup>
                  {selectedProduct.isPopular && <ProductTag>Popular</ProductTag>}
                  {selectedProduct.isPremium && <ProductTag>Premium</ProductTag>}
                  {selectedProduct.isEnergyStar && <ProductTag>Energy Star</ProductTag>}
                  {selectedProduct.isPublished && <ProductTag>Published</ProductTag>}
                  {selectedProduct.category && <ProductTag>{selectedProduct.category}</ProductTag>}
                </ProductBadgeGroup>

                {selectedProduct.description && (
                  <ProductDetailRow>
                    <ProductDetailLabel>Description</ProductDetailLabel>
                    <ProductDetailValue>{selectedProduct.description}</ProductDetailValue>
                  </ProductDetailRow>
                )}

                {selectedProduct.information && (
                  <ProductDetailRow>
                    <ProductDetailLabel>Information</ProductDetailLabel>
                    <ProductDetailValue>{selectedProduct.information}</ProductDetailValue>
                  </ProductDetailRow>
                )}

                {selectedProduct.modelNumber && (
                  <ProductDetailRow>
                    <ProductDetailLabel>Model Number</ProductDetailLabel>
                    <ProductDetailValue>{selectedProduct.modelNumber}</ProductDetailValue>
                  </ProductDetailRow>
                )}

                {selectedProduct.manufacturer && (
                  <ProductDetailRow>
                    <ProductDetailLabel>Manufacturer</ProductDetailLabel>
                    <ProductDetailValue>{selectedProduct.manufacturer}</ProductDetailValue>
                  </ProductDetailRow>
                )}

                {selectedProduct.uniqueCode && (
                  <ProductDetailRow>
                    <ProductDetailLabel>Product Code</ProductDetailLabel>
                    <ProductDetailValue>{selectedProduct.uniqueCode}</ProductDetailValue>
                  </ProductDetailRow>
                )}
              </>
            )}
          </ProductModalBody>
        </ProductModalContent>
      </ProductModal>

      <CustomAlert
        isVisible={alert.isVisible}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={() => setAlert({ isVisible: false, message: '' })}
        onConfirm={alert.onConfirm}
      />
    </PageContainer>
  );
};

export default ViewProductInformation;